import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { RootState, AppDispatch } from '../../redux/store';
import { fetchMatchSummaries, Match, MatchSummary, deleteMatch } from '../../redux/slices/matchesSlice';
import { logout } from '../../redux/slices/authSlice';
import Navigation from '../../components/Navigation';
import MatchImporter from './MatchImporter';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  color: #f0f0f0; /* Default light text */
`;

const AdminHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const AddButton = styled.button`
  background-color: ${({ theme }) => theme.colors.success || '#28a745'};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;

  &:hover {
    opacity: 0.9;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #ffffff;
`;

const LogoutButton = styled.button`
  background-color: ${({ theme }) => theme.colors.danger};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;

  &:hover {
    opacity: 0.9;
  }
`;

const FilterContainer = styled.div`
  margin-bottom: 2rem;
  display: flex;
  gap: 1rem;
`;

const Select = styled.select`
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #555;
  background-color: #2a2a2a;
  color: white;
  font-size: 1rem;
`;

const MatchList = styled.div`
  display: grid;
  gap: 1rem;
`;

const MatchCard = styled.div`
  background: #2a2a2a; /* Dark background */
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  border: 1px solid #444;

  &:hover {
    background: #333;
  }
`;

const MatchInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Teams = styled.div`
  font-weight: bold;
  font-size: 1.1rem;
  color: #ffffff;
`;

const Details = styled.div`
  font-size: 0.9rem;
  color: #ccc; /* Lighter gray for readability on dark */
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const EditButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.accent};
  }
`;

const DeleteButton = styled.button`
  background: ${({ theme }) => theme.colors.danger};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const AdminDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { summaries, matches, loading } = useSelector((state: RootState) => state.matches as any);
  
  // Initialize state from sessionStorage if available, otherwise 'all'
  const [selectedRound, setSelectedRound] = useState<string>(() => {
    return sessionStorage.getItem('adminSelectedRound') || 'all';
  });

  useEffect(() => {
    if (summaries.length === 0 && matches.length === 0) {
      dispatch(fetchMatchSummaries());
    }
  }, [dispatch, summaries.length, matches.length]);

  // Update sessionStorage when selection changes
  useEffect(() => {
    sessionStorage.setItem('adminSelectedRound', selectedRound);
  }, [selectedRound]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const sourceMatches: Array<Match | MatchSummary> = (summaries && summaries.length > 0) ? summaries : matches;
  const rounds: string[] = Array.from(new Set(sourceMatches.map((m) => m.round).filter(Boolean) as string[])).sort((a, b) => Number(a) - Number(b));

  const filteredMatches = (selectedRound === 'all' 
    ? sourceMatches 
    : sourceMatches.filter((m) => m.round === selectedRound)) as Array<Match | MatchSummary>;

  const handleEdit = (id: string) => {
    navigate(`/admin/match/${id}`);
  };

  const handleDelete = async (id: string, teams: string) => {
    if (window.confirm(`Opravdu chcete smazat zápas ${teams}?`)) {
      await dispatch(deleteMatch(id));
    }
  };

  const handleAdd = () => {
    navigate('/admin/match/new');
  };

  if (loading) return <PageContainer>Načítání...</PageContainer>;

  const getStatusLabel = (match: any) => {
    const hasEvents = match.events && match.events.length > 0;
    // If match has events but is scheduled, treat as live (unless we know it's finished)
    // However, for admin, maybe we should show the real status? 
    // User requested "místo scheduled by měl být finished".
    // If we want to show 'live' when events exist, we can. 
    // But if it's 1:0 and over, it should be finished. 
    // We'll stick to 'live' as effective status for scheduled+events.
    const effectiveStatus = (match.status === 'scheduled' && hasEvents) ? 'live' : match.status;

    switch (effectiveStatus) {
      case 'live': return 'ŽIVĚ';
      case 'finished': return 'KONEC';
      case 'awarded': return 'KONTUMACE';
      case 'canceled': return 'ZRUŠENO';
      case 'not_played': return 'NEODEHRÁNO';
      case 'scheduled': return 'NAPLÁNOVÁNO';
      default: return effectiveStatus;
    }
  };

  return (
    <PageContainer>
      <Navigation />
      <AdminHeader>
        <Title>Administrace Zápasů</Title>
        <HeaderButtons>
          <AddButton onClick={handleAdd}>+ Přidat Zápas</AddButton>
          <LogoutButton onClick={handleLogout}>Odhlásit se</LogoutButton>
        </HeaderButtons>
      </AdminHeader>
      
      <MatchImporter onImportSuccess={() => dispatch(fetchMatchSummaries())} />

      <FilterContainer>
        <Select value={selectedRound} onChange={(e) => setSelectedRound(e.target.value)}>
          <option value="all">Všechna kola</option>
          {rounds.map((round: string) => (
            <option key={round} value={round}>{round}. kolo</option>
          ))}
        </Select>
      </FilterContainer>

      <MatchList>
        {filteredMatches.map((match: Match | MatchSummary) => {
          if (!match.homeTeam || !match.awayTeam) {
            return (
              <MatchCard key={match.id}>
                <MatchInfo>
                  <Teams>Neúplná data zápasu (ID: {match.id})</Teams>
                  <Details>Tento zápas má chybějící informace o týmech.</Details>
                </MatchInfo>
                <ActionButtons>
                  <EditButton onClick={() => handleEdit(match.id)}>
                    Opravit
                  </EditButton>
                  <DeleteButton onClick={() => handleDelete(match.id, 's neznámými týmy')}>
                    Smazat
                  </DeleteButton>
                </ActionButtons>
              </MatchCard>
            );
          }
          
          return (
            <MatchCard key={match.id}>
              <MatchInfo>
                <Teams>{match.homeTeam.name} vs {match.awayTeam.name}</Teams>
                <Details>
                  {new Date(match.date).toLocaleDateString()} | {match.score.home}:{match.score.away} | {getStatusLabel(match)}
                </Details>
              </MatchInfo>
              <ActionButtons>
                <EditButton onClick={() => handleEdit(match.id)}>
                  Upravit
                </EditButton>
                <DeleteButton onClick={() => handleDelete(match.id, `${match.homeTeam.name} vs ${match.awayTeam.name}`)}>
                  Smazat
                </DeleteButton>
              </ActionButtons>
            </MatchCard>
          );
        })}
      </MatchList>
    </PageContainer>
  );
};

export default AdminDashboard;
